import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, Spin } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { AxiosError } from 'axios';
import { decode as decodeHtml } from 'he';
import { cloneDeep, isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ebayManagerApi, shippoApi, storageItemApi, storageProcessApi } from '../../../../apis';
import {
  AddressDto,
  CreateShipmentDto,
  CreateShipToBuyerDto,
  ParcelsDtoDistanceUnitEnum,
  ParcelsDtoMassUnitEnum,
  Rate,
  ShippingAddress,
} from '../../../../apis/client-axios';
import { BackIcon } from '../../../../components/@svg/BackIcon';
import { SvgArrowDownSelect } from '../../../../components/@svg/SvgArrowDownSelect';
import ButtonAgree from '../../../../components/buttonCustom/ButtonAgree';
import ButtonAgreeWhite from '../../../../components/buttonCustom/ButtonAgreeWhite';
import ButtonCancel from '../../../../components/buttonCustom/ButtonCancel';
import CustomInput from '../../../../components/input/CustomInput';
import ModalConfirm from '../../../../components/ModalConfirm/ModalConfirm';
import ModalError from '../../../../components/ModalError';
import { ModalInformation } from '../../../../components/ModalInformation';
import NotificationError from '../../../../components/notification/NotificationError';
import NotificationSuccess from '../../../../components/notification/NotificationSuccess';
import { ErrorMessage } from '../../../../components/response/error.message';
import CustomSelect from '../../../../components/select/CustomSelect';
import { StyledModal } from '../../../../components/StyledModal';
import { USER_ROUTE_PATH } from '../../../../constants/router';
import countriesData from '../../../../util/common/countries.json';
import {
  EDIT_FIELD_LABELS,
  EDIT_FIELD_LIMITS,
  EditFieldKey,
  QUERY_KEY,
  EU_COUNTRIES,
} from '../../../../util/constants';
import { containsInvalidCharacters } from '../../../../util/function-util';
import { TMessagesCarrier } from '../../return/storage-request';
import Bill from '../components/Bill';
import Carrier from '../components/Carrier';
import EditDetailBuyer from '../components/EditDetailBuyer';
import MemoInfo from '../components/MemoInfo';
import AddressSuggestionModal from '../components/ModalAddressSuggestion';
import ProductInfo from '../components/ProductInfo';
import ShipInfo from '../components/ShipInfo';
import SizeInfo from '../components/sizeInfo/SizeInfo';
import SizePackage from '../components/SizePackage';
import './ship2buyerRequest.scss';

export type TDataRequest = {
  carrier: string;
  carrierPrice: number;
  errorInputHScode: boolean;
  errorInputItemPrice: boolean;
  HSCode: string;
  insurance: number;
  isInsurance: boolean;
  itemPrice: number;
  openModalInformation: boolean;
  orderAccountEbayId: number;
  orderBuyerEiasToken: string;
  orderItemId: string;
  orderItemName: string;
  orderItemPrice: number;
  orderItemPriceCurrency: string;
  orderItemSKU: string;
  orderStatus: string;
  quantityPurchased: number;
  shippingPlan: string;
  shippingRate: string;
  transactionId: string;
  shipByDate: string;
  confirm: boolean;
  imgVariant: string;
  internalSku: string;
  battery: string;
  isPickUPSOutSide: boolean;
  isSignatureOption: boolean;
  signatureOption?: string;
};

export default function Ship2BuyerRequest() {
  const navigate = useNavigate();

  const { state } = useLocation();
  const itemStorage = state['itemStorage'];
  const orderId = state['orderId'];
  const itemId = state['itemId'];
  const userEbayId = state['userEbayId'];
  const IOSS = state['IOSS'];
  const phone = state['phone'];
  const lineItemID = state['lineItemID'];
  const shipByDate = state['shipByDate'];
  const imageLineItem = state['imageLineItem'];

  const [modalEditAddress, setModalEditAddress] = useState<{
    isOpen: boolean;
    isChange: boolean;
    data: Omit<ShippingAddress, 'AddressID' | 'AddressOwner' | 'CountryName'>;
  }>({
    isOpen: false,
    isChange: false,
    data: {
      Name: '',
      Street1: '',
      Street2: '',
      CityName: '',
      PostalCode: '',
      StateOrProvince: '',
      Country: '',
      Phone: phone,
    },
  });

  const [formEditAddress] = useForm<typeof modalEditAddress.data>();
  const [editAddressFormValues, setEditAddressFormValues] = useState<typeof modalEditAddress.data>(
    modalEditAddress.data,
  );

  const [dataRequest, setDataRequest] = useState<TDataRequest>({
    carrier: '',
    carrierPrice: 0,
    errorInputHScode: false,
    errorInputItemPrice: false,
    HSCode: '',
    insurance: 0,
    isInsurance: false,
    itemPrice: 0,
    openModalInformation: false,
    orderAccountEbayId: 0,
    orderBuyerEiasToken: '',
    orderItemId: '',
    orderItemName: '',
    orderItemPrice: 0,
    orderItemPriceCurrency: '',
    orderItemSKU: '',
    orderStatus: '',
    quantityPurchased: 0,
    shippingPlan: '',
    shippingRate: '',
    transactionId: '',
    shipByDate: '',
    confirm: false,
    imgVariant: '',
    internalSku: '',
    battery: '',
    isPickUPSOutSide: false,
    isSignatureOption: false,
    signatureOption: 'UNNECESSARY',
  });

  const [openModalError, setOpenModalError] = useState({
    isShow: false,
    text: '',
    isLink: false,
    nameModal: '',
  });

  const [modalVisibleRecommendAddress, setModalVisibleRecommendAddress] = useState(false);
  const [suggestedAddress, setSuggestedAddress] = useState({
    Name: '',
    Street1: '',
    Street2: '',
    CityName: '',
    PostalCode: '',
    StateOrProvince: '',
    Country: '',
    Phone: phone,
  });

  const [listCarrier, setListCarrier] = useState<Rate[]>([]);
  const [listCarrierWithInsurance, setListCarrierWithInsurance] = useState([]);

  type SignatureOpt = 'UNNECESSARY' | 'STANDARD' | 'ADULT';
  type ExtraKey = `${0 | 1}_${SignatureOpt}`;

  const buildExtraKey = (isInsurance: boolean, sig?: string): ExtraKey =>
    `${isInsurance ? 1 : 0}_${(sig || 'UNNECESSARY') as SignatureOpt}` as ExtraKey;

  const [carrierByExtra, setCarrierByExtra] = useState<Record<ExtraKey, Rate[]>>({} as Record<ExtraKey, Rate[]>);

  const [previousShipmentBody, setPreviousShipmentBody] = useState<CreateShipmentDto | null>(null);
  const [isEditAddressLimitModalOpen, setIsEditAddressLimitModalOpen] = useState(false);

  const isInvalidSpecialCharacters = (addressData: typeof modalEditAddress.data) => {
    const fieldsToValidate = [
      addressData.Street1,
      addressData.Street2,
      addressData.CityName,
      addressData.StateOrProvince,
      addressData.PostalCode,
    ];
    return fieldsToValidate.some(containsInvalidCharacters);
  };

  const { mutate: CreateShipToBuyer, isPending: loadingCreateShip2Buyer } = useMutation({
    mutationKey: ['submit'],
    mutationFn: (data: CreateShipToBuyerDto) =>
      storageProcessApi.storageProcessControllerCreateShipToBuyerRequest(data, {
        headers: { 'skip-error-handler': true },
      }),
    onSuccess: () => {
      NotificationSuccess({ content: '発送依頼が登録されました。' });
      navigate(`${USER_ROUTE_PATH.RETURN_ITEM}`);
    },
    onError: ({ response: { data } }: AxiosError) => {
      if ((data as any)?.message.includes('LABEL_CREATED_ERROR')) {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: true,
          text: '<p>ラベル発行が失敗しました。<br />カスタマーサービスにお問合せください。</p>',
          nameModal: 'LABEL_CREATED_ERROR',
        });
      } else if ((data as any)?.message.includes('CHARGE_MONEY_FAILED')) {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: false,
          text: '<p>決済が失敗しました。<br />カード情報を確認してください。</p>',
          nameModal: 'CHARGE_MONEY_FAILED',
        });
      } else if ((data as any)?.message.includes('HAVE_PAYMENT_NOT_FINISH')) {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: true,
          text: '<p>未支払料金があります。<br />カスタマーサービスにお問合せください。</p>',
          nameModal: 'HAVE_PAYMENT_NOT_FINISH',
        });
      } else {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: true,
          text: '<p>エラーが発生しました。<br />カスタマーサービスにお問合せください。</p>',
          nameModal: 'BAYPACK_ERROR',
        });
      }
    },
  });

  const {
    data: itemDetail,
    mutate: getOneItem,
    isPending: loadingGetOne,
  } = useMutation({
    mutationKey: [itemStorage],
    mutationFn: (id: string) => storageItemApi.storageItemControllerGetOneItemManagementUser(id),
  });

  const {
    data: carrier,
    mutate: getCarrier,
    isPending: loadingCarrier,
  } = useMutation({
    mutationKey: ['carrier'],
    mutationFn: (data: CreateShipmentDto) =>
      shippoApi.shippoApiControllerCreateShipment(data, {
        headers: { 'skip-error-handler': true },
      }),
    onSuccess: ({ data }, variables) => {
      const listRates = Object.values(data.rates).filter(Boolean) as Rate[];

      const isInsurance = !!variables?.extra?.insurance;
      const signatureOption = variables?.extra?.signature_confirmation || 'UNNECESSARY';
      const key = buildExtraKey(isInsurance, signatureOption);

      setCarrierByExtra((prev) => ({
        ...prev,
        [key]: listRates,
      }));

      if (!variables.extra) {
        setListCarrier(listRates);
      } else {
        setListCarrierWithInsurance(listRates);
      }

      if (!listRates.length) {
        const messages =
          data?.messages?.filter(
            (v) => v['text'] !== 'Shipment origin is out of service area for UPS Master account from CA',
          ) || [];

        messages.length
          ? messages.forEach((v: TMessagesCarrier) => {
              const split = v.text.split(': ');
              const content = `${v.source}: ${split[1] ?? split[0] ?? 'ERROR'}`;
              NotificationError({ content });
            })
          : NotificationError({ content: ErrorMessage[500] });
      }
    },
    onError: ({ response: { data } }: AxiosError) => {
      if ((data as any)?.message.includes('LABEL_CREATED_ERROR')) {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: true,
          text: '<p>ラベル発行が失敗しました。<br />カスタマーサービスにお問合せください。</p>',
          nameModal: 'LABEL_CREATED_ERROR',
        });
      } else if ((data as any)?.message.includes('CHARGE_MONEY_FAILED')) {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: false,
          text: '<p>決済が失敗しました。<br />カード情報を確認してください。</p>',
          nameModal: 'CHARGE_MONEY_FAILED',
        });
      } else if ((data as any)?.message.includes('HAVE_PAYMENT_NOT_FINISH')) {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: true,
          text: '<p>未支払料金があります。<br />カスタマーサービスにお問合せください。</p>',
          nameModal: 'HAVE_PAYMENT_NOT_FINISH',
        });
      } else {
        setOpenModalError({
          ...openModalError,
          isShow: true,
          isLink: true,
          text: '<p>エラーが発生しました。<br />カスタマーサービスにお問合せください。。</p>',
          nameModal: 'BAYPACK_ERROR',
        });
      }
    },
  });

  const {
    data: orderEbay,
    mutate: getOneOrder,
    isPending: loadingGetOneOrder,
  } = useMutation({
    mutationKey: [orderId],
    mutationFn: (data: { orderId: string; userEbayId: string }) =>
      ebayManagerApi.ebayManagerControllerGetOneOrderReturnFromEbay(data.orderId, data.userEbayId),
    onError: ({ response: { data } }: AxiosError) => {},
  });

  const fetchApi = async () => await Promise.all([getOneItem(itemStorage), getOneOrder({ orderId, userEbayId })]);

  const { data: listAccountEbay } = useQuery({
    queryKey: [QUERY_KEY.LIST_ACCOUNT_EBAY],
    queryFn: () =>
      ebayManagerApi.ebayManagerControllerGetAllAccountEbay(1, undefined, undefined, undefined, undefined, false),
    staleTime: 1000,
  });

  const { data: listCountries } = useQuery({
    queryKey: ['listCountries'],
    queryFn: () =>
      Promise.resolve(
        countriesData
          .map((v: any) => ({
            label: v?.name,
            value: v?.alpha2Code,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ),
  });

  const { mutate: getAddressRecommend } = useMutation({
    mutationKey: ['recommendAddress'],
    mutationFn: (data: AddressDto) => shippoApi.shippoApiControllerRecommendAddress(data),
    onSuccess: ({ data }) => {
      if (data) {
        setModalVisibleRecommendAddress(true);
        setSuggestedAddress({
          Name: data.name,
          Street1: data?.address_line_1 || data?.address_line_2,
          Street2: data?.address_line_2,
          CityName: data?.city_locality,
          PostalCode: data?.postal_code,
          StateOrProvince: data?.state_province,
          Country: data?.country_code,
          Phone: modalEditAddress?.data?.Phone,
        });
      } else {
        validateAndHandleEditAddressLimits(modalEditAddress.data, { openModalOnError: true });
      }
    },
  });

  const handleClickBack = () => {
    return dataRequest.confirm ? setDataRequest({ ...dataRequest, confirm: false }) : navigate(-1);
  };

  const buildExtra = (data: TDataRequest): CreateShipmentDto['extra'] | undefined => {
    const hasInsurance = data.isInsurance;
    const hasSignature = !!data.signatureOption && data.signatureOption !== 'UNNECESSARY';

    if (!hasInsurance && !hasSignature) return undefined;

    const signatureMap: Record<string, string> = {
      STANDARD: 'STANDARD',
      ADULT: 'ADULT',
    };

    return {
      ...(hasInsurance && {
        insurance: {
          amount: data.itemPrice.toString(),
          currency: 'USD',
        },
      }),
      ...(hasSignature && {
        signature_confirmation: signatureMap[data.signatureOption],
      }),
    };
  };

  const handleSubmit = () => {
    let hasErrorSubmit = false;

    // validate tên hàng
    if (dataRequest.orderItemName === '' || dataRequest.orderItemName.length > 100) {
      const elements = document.getElementsByClassName('item-name');
      if (dataRequest.orderItemName.length > 100) {
        NotificationError({
          content: '商品情報は100文字以内にしてください',
        });
      }
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLElement;
        element.classList.add('error-border');
      }
      hasErrorSubmit = true;
    }

    // validate giá
    if (!dataRequest.itemPrice) {
      const elements = document.getElementsByClassName('is-price');
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLElement;
        element.classList.add('error-border');
      }

      setDataRequest({
        ...dataRequest,
        errorInputItemPrice: !dataRequest.itemPrice,
      });
      hasErrorSubmit = true;
    }

    // validate HSCode nếu không phải US
    if (orderEbay?.data?.ShippingAddress?.Country !== 'US') {
      if (!dataRequest.HSCode || dataRequest.HSCode.length > 50) {
        if (dataRequest.HSCode.length > 50) {
          NotificationError({
            content: 'HSコードは50文字以内にしてください',
          });
        }
        const elements = document.getElementsByClassName('is-code');
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i] as HTMLElement;
          element.classList.add('error-border');
        }
        setDataRequest({
          ...dataRequest,
          errorInputHScode: !dataRequest.HSCode,
        });
        hasErrorSubmit = true;
      }
    }

    // validate chọn carrier
    if (dataRequest.carrier === '') {
      const elements = document.getElementsByClassName('box-carrier');
      for (let i = 0; i < elements.length; i++) {
        const element = elements[i] as HTMLElement;
        element.classList.add('error-border');
      }
      hasErrorSubmit = true;
    }

    if (hasErrorSubmit) {
      NotificationError({ content: '入力必須項目をすべて入力してください。' });
      return;
    }

    // ===== BƯỚC 2: ĐÃ CONFIRM -> GỬI REQUEST THẬT =====
    if (dataRequest.confirm) {
      const mapAddress = (ShippingAddress: typeof modalEditAddress.data) => {
        const addressLine1 = ShippingAddress?.Street1 || '';
        const addressLine2 = ShippingAddress?.Street2 || '';
        const city = ShippingAddress?.CityName || '';
        const postalCode = ShippingAddress?.PostalCode || '';
        const stateOrProvince = ShippingAddress?.StateOrProvince || '';

        return `${addressLine1}, ${addressLine2 ? `${addressLine2},` : ''} ${city}, ${stateOrProvince} ${postalCode}`;
      };

      CreateShipToBuyer({
        hsCode: dataRequest.HSCode,
        ioss: IOSS ?? '',
        isInsurance: dataRequest.isInsurance,
        itemPrice: dataRequest.itemPrice ?? dataRequest.orderItemPrice,
        orderAccountEbayId: dataRequest.orderAccountEbayId,
        orderBuyerCountry: modalEditAddress.data.Country,
        orderBuyerEiasToken: dataRequest.orderBuyerEiasToken,
        orderBuyerId: orderEbay?.data?.BuyerUserID,
        orderId: orderId,
        orderItemId: dataRequest.orderItemId,
        orderItemName: decodeHtml(dataRequest.orderItemName || ''),
        orderItemPrice: dataRequest.orderItemPrice,
        orderItemPriceCurrency: dataRequest.orderItemPriceCurrency,
        orderItemSKU: dataRequest.orderItemSKU,
        orderShippingAddress: modalEditAddress.data.Street1,
        orderShippingAddress2: modalEditAddress.data.Street2,
        orderShippingName: modalEditAddress.data.Name,
        orderShippingTelNo: modalEditAddress.data.Phone,
        orderShippingZipCode: modalEditAddress.data.PostalCode,
        orderStatus: dataRequest.orderStatus,
        orderTransactionId: dataRequest.transactionId,
        quantityPurchased: dataRequest.quantityPurchased,
        shippingAddress: mapAddress(modalEditAddress.data),
        shippingAddress2: modalEditAddress.data.Street2,
        shippingCarrier: dataRequest.carrier,
        shippingName: modalEditAddress.data.Name,
        shippingPlan: dataRequest.shippingPlan,
        shippingRate: dataRequest.shippingRate,
        shippingTelNo: modalEditAddress.data.Phone,
        shippingZipCode: modalEditAddress.data.PostalCode,
        storageItemId: +itemStorage,
        lineItemId: lineItemID,
        shipByDate: dataRequest.shipByDate,
        battery: dataRequest?.battery.includes('バッテリーなし') ? null : dataRequest?.battery,
      });

      return;
    }

    // ===== BƯỚC 1: CHƯA CONFIRM -> CHECK GIÁ LẠI VỚI SHIPPO =====

    if (isInvalidSpecialCharacters(modalEditAddress.data)) {
      NotificationError({
        content: '住所は特殊文字を含むことはできませんので修正をお願いします。',
      });
      return;
    }

    // Nếu country = US, name + price không đổi => bỏ qua bước confirm, cho qua luôn
    if (modalEditAddress.data.Country === 'US') {
      const isSamePrice = String(dataRequest?.itemPrice) === String(dataRequest?.orderItemPrice);
      const normalize = (str?: string) => (str ?? '').trim();
      const isSameName = normalize(dataRequest?.orderItemName) === normalize(itemDetail?.data?.itemEditName);

      if (isSamePrice && isSameName) {
        setDataRequest((prev) => ({ ...prev, confirm: true }));
        return;
      }
    }

    let bodyGetShipment: CreateShipmentDto = {
      ebayAccountId: itemDetail?.data?.ebayReturns?.accountEbayId,
      address_to: {
        name: modalEditAddress.data.Name,
        street1: modalEditAddress.data.Street1 || modalEditAddress.data.Street2,
        street2: modalEditAddress.data.Street2 ?? '',
        city: modalEditAddress.data.CityName,
        state: modalEditAddress.data.StateOrProvince,
        zip: modalEditAddress.data.PostalCode,
        country: modalEditAddress.data.Country,
        phone: modalEditAddress.data.Phone,
      },
      parcels: [
        {
          distance_unit: ParcelsDtoDistanceUnitEnum.Cm,
          height: String(itemDetail?.data?.storageHeight),
          length: String(itemDetail?.data?.storageLength),
          mass_unit: ParcelsDtoMassUnitEnum.Kg,
          weight: String(itemDetail?.data?.storageWeight),
          width: String(itemDetail?.data?.storageWidth),
          metadata: '',
        },
      ],
      customs_declaration: {
        items: [
          {
            quantity: 1,
            mass_unit: 'kg',
            origin_country: 'US',
            hs_code: dataRequest.HSCode,
            sku_code: itemDetail?.data?.itemSku,
            net_weight: String(itemDetail?.data?.storageWeight),
            value_amount: String(dataRequest.itemPrice),
            value_currency: 'USD',
            description: (dataRequest.orderItemName ?? itemDetail?.data?.itemEditName)?.slice(0, 30) || '',
          },
        ],
        commercial_invoice: dataRequest?.isPickUPSOutSide ? true : false,
        eel_pfc: modalEditAddress.data.Country === 'CA' ? 'NOEEI_30_36' : 'NOEEI_30_37_a',
      },
      extra: buildExtra(dataRequest),
    };

    if (IOSS && IOSS !== '') {
      bodyGetShipment = {
        ...bodyGetShipment,
        customs_declaration: {
          ...bodyGetShipment.customs_declaration,
          exporter_identification: {
            tax_id: {
              type: EU_COUNTRIES.includes(modalEditAddress.data.Country) ? 'IOSS' : 'VAT',
              number: IOSS.replace(/\s+/g, ''),
            },
          },
          eel_pfc: modalEditAddress.data.Country === 'CA' ? 'NOEEI_30_36' : 'NOEEI_30_37_a',
        },
      };
    }

    if (isEqual(previousShipmentBody, bodyGetShipment)) {
      setDataRequest((prev) => ({ ...prev, confirm: true }));
      return;
    }

    setPreviousShipmentBody(bodyGetShipment);

    // Chỉ ở đây mới gọi lại Shippo để check giá lần cuối
    getCarrier(bodyGetShipment, {
      onSuccess: (res) => {
        const newRates = Object.values(res?.data?.rates ?? {}) as Rate[];

        const matchedCarrier = newRates.find((rate) => rate?.provider === dataRequest.carrier);

        if (!matchedCarrier) {
          NotificationError({
            content: '選択した配送業者の料金が変更されたため、再度選択してください。',
          });
          setDataRequest((prev) => ({
            ...prev,
            carrier: '',
            carrierPrice: 0,
            shippingPlan: '',
            shippingRate: '',
            confirm: false,
          }));
          return;
        }

        const newPrice = Number(matchedCarrier.amount);

        setDataRequest((prev) => {
          const oldPrice = prev.carrierPrice;
          const priceChanged = !!oldPrice && oldPrice !== newPrice;

          if (priceChanged) {
            NotificationError({
              content: `配送料金が${oldPrice}ドルから${newPrice}ドルに変更されました。内容をご確認ください。`,
            });

            return {
              ...prev,
              carrierPrice: newPrice,
              shippingRate: matchedCarrier.objectId,
              confirm: false,
            };
          }

          // Giá không đổi => cho qua màn confirm
          return {
            ...prev,
            carrierPrice: newPrice,
            shippingRate: matchedCarrier.objectId,
            confirm: true,
          };
        });
      },
    });
  };

  const validateEditField = (values: Record<EditFieldKey, any>) => {
    type FieldErr = { name: (string | number)[]; errors: string[] };
    const fieldErrors: FieldErr[] = [];
    const violatedKeys: (keyof typeof EDIT_FIELD_LIMITS)[] = [];

    (Object.keys(EDIT_FIELD_LIMITS) as (keyof typeof EDIT_FIELD_LIMITS)[]).forEach((key) => {
      const limit = EDIT_FIELD_LIMITS[key];
      const val = (values[key] ?? '') as string;
      if ((val && String(val).length > limit) || containsInvalidCharacters(val)) {
        fieldErrors.push({ name: [key], errors: [] });
        violatedKeys.push(key);
      }
    });

    return { fieldErrors, violatedKeys };
  };

  const validateAndHandleEditAddressLimits = (
    addressData: typeof modalEditAddress.data,
    options?: { openModalOnError?: boolean },
  ) => {
    const { fieldErrors, violatedKeys } = validateEditField(addressData as Record<EditFieldKey, any>);
    if (!violatedKeys.length) return;
    formEditAddress.setFields(fieldErrors);

    if (options?.openModalOnError) {
      setIsEditAddressLimitModalOpen(true);
    }
  };

  const handleFinishEditAddress = (data: typeof modalEditAddress.data) => {
    const fieldsToValidate = [
      data.Street1,
      data.Street2,
      data.CityName,
      data.StateOrProvince,
      data.PostalCode,
      data.Phone,
    ];

    let hasError = false;

    if (fieldsToValidate.some(containsInvalidCharacters)) {
      NotificationError({ content: '住所は特殊文字を含むことはできませんので修正をお願いします。' });
      hasError = true;
    }

    const notifyFieldLimitEdit = (key: keyof typeof EDIT_FIELD_LIMITS) => {
      const limit = EDIT_FIELD_LIMITS[key]!;
      NotificationError({ content: `${EDIT_FIELD_LABELS[key]}は${limit}文字以内にしてください` });
    };

    const { fieldErrors, violatedKeys } = validateEditField(data as Record<EditFieldKey, any>);

    if (violatedKeys.length) {
      formEditAddress.setFields(fieldErrors);
      violatedKeys.forEach((k) => notifyFieldLimitEdit(k));
      formEditAddress.scrollToField(fieldErrors[0].name);
      hasError = true;
    } else {
      formEditAddress.setFields(
        (Object.keys(EDIT_FIELD_LIMITS) as (keyof typeof EDIT_FIELD_LIMITS)[]).map((k) => ({
          name: [k],
          errors: [],
        })),
      );
    }
    if (hasError) return;

    const isChange = Object.keys(data).some((key) => {
      const newVal = typeof data[key] === 'string' ? data[key].trim() : data[key];
      const oldVal =
        typeof modalEditAddress.data[key] === 'string' ? modalEditAddress.data[key].trim() : modalEditAddress.data[key];
      return newVal !== oldVal;
    });

    if (!isChange) {
      setModalEditAddress({ ...modalEditAddress, isOpen: false });
      return;
    }

    setModalEditAddress({
      data: isChange ? data : { ...modalEditAddress.data },
      isChange,
      isOpen: false,
    });
  };

  const handleCloseModalError = () => {
    setOpenModalError({ ...openModalError, isShow: false });
  };

  const handleConfirmSuggestedAddress = (selected) => {
    let nextData = modalEditAddress.data;
    if (selected?.type === 'suggested') {
      nextData = suggestedAddress;
      setModalEditAddress({ ...modalEditAddress, data: suggestedAddress, isChange: true });
    }
    setModalVisibleRecommendAddress(false);
    formEditAddress.setFieldsValue(nextData);
    validateAndHandleEditAddressLimits(nextData, { openModalOnError: true });
  };

  useEffect(() => {
    if (orderId && itemStorage && userEbayId) fetchApi();
  }, [orderId, itemStorage, userEbayId]);

  useEffect(() => {
    if (orderEbay?.data?.BuyerUserID && itemDetail?.data?.id) {
      const Transaction = orderEbay?.data?.TransactionArray?.Transaction as any;
      const item = Array.isArray(Transaction) ? Transaction?.find((v) => v.Item.ItemID === itemId) : Transaction;

      const rawItemPrice = +item?.TransactionPrice?.['_@ttribute'];
      const orderItemPrice = dataRequest?.itemPrice || rawItemPrice;

      const orderShippingTelNo =
        orderEbay?.data?.ShippingAddress?.Phone !== 'Invalid Request' ? orderEbay?.data?.ShippingAddress?.Phone : phone;

      const userAccountId = listAccountEbay?.data?.content.find((item) => item.userEbayId === userEbayId);

      setDataRequest((prev) => ({
        ...prev,
        itemPrice: prev.itemPrice || rawItemPrice,
        insurance: +((1.25 * (prev.itemPrice || rawItemPrice)) / 100).toFixed(2),
        orderAccountEbayId: userAccountId?.id,
        orderBuyerEiasToken: orderEbay?.data?.EIASToken,
        orderItemId: item?.Item?.ItemID,
        orderItemName: decodeHtml(item?.Item?.Title || ''),
        orderItemPrice: orderItemPrice,
        orderItemPriceCurrency: item?.TransactionPrice?.currencyID,
        orderItemSKU: item?.Variation?.SKU || item?.Item?.SKU,
        orderStatus: orderEbay?.data?.OrderStatus,
        quantityPurchased: +item?.QuantityPurchased,
        transactionId: item?.TransactionID,
        shipByDate: shipByDate,
        imgVariant: imageLineItem,
        internalSku: itemDetail?.data?.internalSku,
      }));

      if (orderEbay?.data?.ShippingAddress) {
        const data = cloneDeep(modalEditAddress.data);
        Object.keys(modalEditAddress.data).forEach((key) => {
          data[key] = key === 'Phone' ? orderShippingTelNo : orderEbay?.data?.ShippingAddress?.[key] || '';
        });
        setModalEditAddress({ ...modalEditAddress, data });
      }
    }
  }, [itemDetail, orderEbay, listAccountEbay]);

  // useEffect fetch carrier ban đầu (giữ nguyên behaviour, không dính gì tới signature)
  useEffect(() => {
    if (
      itemDetail?.data?.id &&
      dataRequest.orderItemId &&
      !dataRequest.errorInputHScode &&
      !dataRequest.errorInputItemPrice
    ) {
      const fieldsToValidate = [
        modalEditAddress.data.Street1,
        modalEditAddress.data.Street2,
        modalEditAddress.data.CityName,
        modalEditAddress.data.StateOrProvince,
        modalEditAddress.data.PostalCode,
      ];

      if (fieldsToValidate.some(containsInvalidCharacters)) {
        const elements = document.getElementsByClassName('info-item__editTracking');
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i] as HTMLElement;
          element.style.border = '1px solid #ff4d4f';
        }
      } else {
        const elements = document.getElementsByClassName('info-item__editTracking');
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i] as HTMLElement;
          element.style.border = '';
        }
      }

      let bodyGetShipment: CreateShipmentDto = {
        ebayAccountId: itemDetail?.data?.ebayReturns?.accountEbayId,
        address_to: {
          name: modalEditAddress.data.Name,
          street1: modalEditAddress.data.Street1 || modalEditAddress.data.Street2,
          street2: modalEditAddress.data.Street2 ?? '',
          city: modalEditAddress.data.CityName,
          state: modalEditAddress.data.StateOrProvince,
          zip: modalEditAddress.data.PostalCode,
          country: modalEditAddress.data.Country,
          phone: modalEditAddress.data.Phone,
        },
        parcels: [
          {
            distance_unit: ParcelsDtoDistanceUnitEnum.Cm,
            height: String(itemDetail?.data?.storageHeight),
            length: String(itemDetail?.data?.storageLength),
            mass_unit: ParcelsDtoMassUnitEnum.Kg,
            weight: String(itemDetail?.data?.storageWeight),
            width: String(itemDetail?.data?.storageWidth),
            metadata: '',
          },
        ],
        customs_declaration: {
          items: [
            {
              quantity: 1,
              description: itemDetail?.data?.itemEditName?.slice(0, 30) || '',
              mass_unit: 'kg',
              origin_country: 'US',
              hs_code: dataRequest.HSCode,
              sku_code: itemDetail?.data?.itemSku,
              net_weight: String(itemDetail?.data?.storageWeight),
              value_amount: String(dataRequest.itemPrice),
              value_currency: 'USD',
            },
          ],
          eel_pfc: modalEditAddress.data.Country === 'CA' ? 'NOEEI_30_36' : 'NOEEI_30_37_a',
        },
      };

      const { street1, street2, city, state, zip } = bodyGetShipment.address_to;
      const fieldsToValidate2 = [street1, street2, city, state, zip];

      if (fieldsToValidate2.some(containsInvalidCharacters)) {
        getCarrier(undefined);
        setCarrierByExtra({} as Record<ExtraKey, Rate[]>);
        setListCarrier([]);
        setListCarrierWithInsurance([]);
      } else {
        const baseBody = bodyGetShipment;

        const combos: Array<{ isInsurance: boolean; signatureOption: SignatureOpt }> = [
          { isInsurance: false, signatureOption: 'UNNECESSARY' },
          { isInsurance: false, signatureOption: 'STANDARD' },
          { isInsurance: false, signatureOption: 'ADULT' },
          { isInsurance: true, signatureOption: 'UNNECESSARY' },
          { isInsurance: true, signatureOption: 'STANDARD' },
          { isInsurance: true, signatureOption: 'ADULT' },
        ];

        combos.forEach(({ isInsurance, signatureOption }) => {
          const extra = buildExtra({
            ...dataRequest,
            isInsurance,
            signatureOption,
          } as TDataRequest);

          const body: CreateShipmentDto = extra ? { ...baseBody, extra } : baseBody;
          const key = buildExtraKey(isInsurance, signatureOption);

          getCarrier(body, {
            onSuccess: ({ data }) => {
              const listRates = Object.values(data.rates).filter(Boolean) as Rate[];

              setCarrierByExtra((prev) => ({
                ...prev,
                [key]: listRates,
              }));

              if (!extra) {
                setListCarrier(listRates);
              } else {
                setListCarrierWithInsurance(listRates);
              }

              if (!listRates.length) {
                const messages =
                  data?.messages?.filter(
                    (v) => v['text'] != 'Shipment origin is out of service area for UPS Master account from CA',
                  ) || [];

                messages.length
                  ? messages?.forEach((v: TMessagesCarrier) => {
                      const split = v.text.split(': ');
                      const content = `${v.source}: ${split[1] ?? split[0] ?? 'ERROR'}`;
                      NotificationError({ content });
                    })
                  : NotificationError({ content: ErrorMessage[500] });
              }
            },
          });
        });
      }

      if (IOSS && IOSS !== '') {
        bodyGetShipment = {
          ...bodyGetShipment,
          customs_declaration: {
            ...bodyGetShipment.customs_declaration,
            exporter_identification: {
              tax_id: {
                type: EU_COUNTRIES.includes(modalEditAddress.data.Country) ? 'IOSS' : 'VAT',
                number: IOSS.replace(/\s+/g, ''),
              },
            },
            eel_pfc: modalEditAddress.data.Country === 'CA' ? 'NOEEI_30_36' : 'NOEEI_30_37_a',
          },
        };
      }
    }
  }, [itemDetail, modalEditAddress.data]);

  useEffect(() => {
    if (modalEditAddress.isOpen) {
      formEditAddress.setFieldsValue(modalEditAddress.data);
      setEditAddressFormValues(modalEditAddress.data);
    }
  }, [modalEditAddress.isOpen]);

  useEffect(() => {
    const bodyGetShipment: AddressDto = {
      name: modalEditAddress.data.Name,
      street1: modalEditAddress.data.Street1 || modalEditAddress.data.Street2 || '',
      street2: modalEditAddress.data.Street2 || '',
      city: modalEditAddress.data.CityName,
      state: modalEditAddress.data.StateOrProvince,
      zip: modalEditAddress.data.PostalCode,
      country: modalEditAddress.data.Country,
    };

    getAddressRecommend(bodyGetShipment);
  }, [modalEditAddress?.data]);

  useEffect(() => {
    if (dataRequest.orderItemName) {
      const elements = document.getElementsByClassName('item-name');
      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).classList.remove('error-border');
      }
    }
    if (dataRequest.HSCode) {
      const elements = document.getElementsByClassName('is-code');
      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).classList.remove('error-border');
      }
    }

    if (dataRequest.itemPrice) {
      const elements = document.getElementsByClassName('is-price');
      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).classList.remove('error-border');
      }
    }

    if (dataRequest.carrierPrice) {
      const elements = document.getElementsByClassName('box-carrier');
      for (let i = 0; i < elements.length; i++) {
        (elements[i] as HTMLElement).classList.remove('error-border');
      }
    }
  }, [dataRequest.HSCode, dataRequest.itemPrice, dataRequest.carrierPrice, dataRequest.orderItemName]);

  const currentExtraKey = buildExtraKey(dataRequest.isInsurance, dataRequest.signatureOption as SignatureOpt);

  const currentCarrierList =
    carrierByExtra[currentExtraKey] ??
    (dataRequest.isInsurance || (dataRequest.signatureOption && dataRequest.signatureOption !== 'UNNECESSARY')
      ? listCarrierWithInsurance
      : listCarrier);

  return (
    <div className="request-ship-2-buyer">
      {itemDetail?.data && (
        <>
          <div className="request-ship-2-buyer__btn-back" onClick={handleClickBack}>
            <BackIcon />
          </div>
          <div className="request-ship-2-buyer-container">
            <p className="title">{dataRequest.confirm ? '発送依頼（最終確認）' : '発送依頼（配送情報）'}</p>
            <div className="content-ship">
              <div className="left">
                {dataRequest.confirm && (
                  <ProductInfo
                    src={dataRequest.imgVariant}
                    itemName={dataRequest?.orderItemName}
                    itemId={dataRequest?.orderItemId}
                    itemSku={dataRequest?.orderItemSKU}
                    price={dataRequest?.itemPrice}
                    internalSku={dataRequest?.internalSku}
                    accountEbay={userEbayId}
                  />
                )}
                <div className="left__detail-ship">
                  {dataRequest.confirm && <ShipInfo orderEbay={orderEbay} />}
                  <EditDetailBuyer
                    orderEbay={orderEbay}
                    modalEditAddress={modalEditAddress}
                    setModalEditAddress={setModalEditAddress}
                    dataRequest={dataRequest}
                  />
                </div>
                {!dataRequest.confirm ? (
                  <>
                    <SizePackage
                      dataRequest={dataRequest}
                      itemDetail={itemDetail}
                      setDataRequest={setDataRequest}
                      orderEbay={orderEbay}
                    />
                    <Carrier
                      carrier={currentCarrierList}
                      loadingCarrier={loadingCarrier}
                      dataRequest={dataRequest}
                      setDataRequest={setDataRequest}
                      itemDetail={itemDetail}
                      modalEditAddress={modalEditAddress}
                    />
                  </>
                ) : (
                  <div className="left__detail-ship">
                    <SizeInfo itemDetail={itemDetail} title="パッケージ情報" />
                    <MemoInfo itemDetail={itemDetail} />
                  </div>
                )}
              </div>
              <div className="right">
                <Bill
                  dataRequest={dataRequest}
                  handleClickBack={handleClickBack}
                  handleSubmit={handleSubmit}
                  carrier={carrier}
                  orderByCountry={orderEbay?.data?.ShippingAddress?.Country}
                />
              </div>
            </div>
          </div>
          {(loadingGetOne || loadingGetOneOrder || loadingCarrier || loadingCreateShip2Buyer) && (
            <div className="loading">
              <Spin />
            </div>
          )}

          <ModalInformation
            content={
              'USPSは梱包サイズや重量が違うと、追加料金が発生しやすい傾向があります。USPSが1割ほど安いくらいの違いであれば、USPS以外をお勧めします。'
            }
            onCancel={() => setDataRequest({ ...dataRequest, openModalInformation: false })}
            open={dataRequest.openModalInformation}
            title={'USPS'}
          />

          {openModalError.isShow && (
            <ModalError
              isOpen={openModalError.isShow}
              handleCancelModal={handleCloseModalError}
              isLink={openModalError.isLink}
              nameModal={openModalError.nameModal}
              className={`${openModalError.nameModal.includes('CHARGE_MONEY_FAILED') ? 'charge-error' : ''}`}
              idItemDetail={itemDetail?.data?.id}
            >
              <div dangerouslySetInnerHTML={{ __html: openModalError.text }} />
            </ModalError>
          )}

          <StyledModal
            children={
              <Form
                form={formEditAddress}
                onFinish={handleFinishEditAddress}
                className="request-ship-2-buyer__modal-edit-address-form"
                layout="vertical"
                onValuesChange={(changed) => {
                  const [key, val] = Object.entries(changed)[0] as [EditFieldKey, any];
                  if (!key) return;
                  setEditAddressFormValues((prev) => ({
                    ...prev,
                    [key]: val,
                  }));
                  formEditAddress.setFields([{ name: [key], errors: [] }]);
                }}
              >
                {Object.keys(modalEditAddress.data).map((key) => {
                  const k = key as EditFieldKey;
                  const isOptional = k === 'Street2' || k === 'StateOrProvince';
                  const isPhone = k === 'Phone';
                  const isCountry = k === 'Country';
                  const limit = EDIT_FIELD_LIMITS[k];
                  const value = (editAddressFormValues as any)?.[k] ?? '';
                  const isErrorLength = !!limit && !!value && String(value).length > limit;
                  const isErrorSpecialCharacters = containsInvalidCharacters(value);
                  const helpMessages = [
                    isErrorLength ? EDIT_FIELD_LABELS[k] + 'は' + limit + '文字以内にしてください' : null,
                    isErrorSpecialCharacters
                      ? `特殊文字が含まれていますので、修正をお願いします。
（半角英数字および + , - . ; : ( ) _ " ' [ ] \\ / ~ のみ使用できます。）`
                      : null,
                  ].filter(Boolean) as string[];

                  return (
                    <Form.Item
                      key={k}
                      name={k}
                      label={EDIT_FIELD_LABELS[k]}
                      help={
                        helpMessages.length > 0 ? (
                          <span style={{ whiteSpace: 'pre-line' }}>{helpMessages.join('\n')}</span>
                        ) : undefined
                      }
                      validateStatus={isErrorLength || isErrorSpecialCharacters ? 'error' : undefined}
                      rules={
                        isPhone
                          ? [
                              {
                                required: true,
                                message: '',
                                transform: (value) => (value ? value.replace(/[^\d]/g, '') : value),
                              },
                            ]
                          : isOptional
                            ? undefined
                            : [
                                {
                                  required: true,
                                  message: '',
                                  transform: (value) => (value ? value.trim() : value),
                                },
                              ]
                      }
                    >
                      {isCountry ? (
                        <CustomSelect
                          className="item-country"
                          suffixIcon={<SvgArrowDownSelect />}
                          optionLabelProp="label"
                          options={listCountries}
                          placeholder="選択してください"
                          showSearch
                          optionFilterProp="label"
                        />
                      ) : (
                        <CustomInput autoComplete="off" type="text" />
                      )}
                    </Form.Item>
                  );
                })}
              </Form>
            }
            isOpen={modalEditAddress.isOpen}
            onCancel={() => setModalEditAddress({ ...modalEditAddress, isOpen: false })}
            modalProps={{
              closable: true,
              title: 'バイヤー情報編集',
              className: 'request-ship-2-buyer__modal-edit-address',
              footer: (
                <>
                  <ButtonCancel
                    value="キャンセル"
                    onClick={() => setModalEditAddress({ ...modalEditAddress, isOpen: false })}
                  />
                  <ButtonAgreeWhite
                    value="元に戻す"
                    onClick={() => formEditAddress.setFieldsValue(orderEbay?.data?.ShippingAddress)}
                  />
                  <ButtonAgree
                    value="保存"
                    onClick={async () => {
                      try {
                        const values = await formEditAddress.validateFields();
                        handleFinishEditAddress(values);
                      } catch (e) {}
                    }}
                  />
                </>
              ),
            }}
          />

          <AddressSuggestionModal
            visible={modalVisibleRecommendAddress}
            dataOrigin={modalEditAddress.data}
            dataSuggested={suggestedAddress}
            onClose={() => setModalVisibleRecommendAddress(false)}
            onConfirm={(selected) => {
              handleConfirmSuggestedAddress(selected);
            }}
          />

          <ModalConfirm
            isOpen={isEditAddressLimitModalOpen}
            title={'バイヤーの住所にエラーがあります。修正をお願いします。'}
            handleCancelModal={() => {
              setIsEditAddressLimitModalOpen(false);
              setModalEditAddress((prev) => ({
                ...prev,
                isOpen: true,
              }));
            }}
            textCancel="OK"
            wrapClassName="modal-confirm-wrap"
            className="confirm-modal-common confirm-modal-edit-address-limit"
          />
        </>
      )}
    </div>
  );
}
